import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import LazyImage from "../lazy-image";
import { ProductItemInterface } from "@/assets/interfaces";
import CanvasBgFromVideo from "./canvas-bg-from-video";
import classNames from "classnames";
interface ProductGalleryProps {
  images: string[]; // Array of image URLs
  videos: ProductItemInterface["videos"];
  shareAction?: VoidFunction;
}

enum MediaType {
  Image = "image",
  Video = "video",
}

interface MediaItem {
  type: MediaType;
  url: string;
  thumbnail?: string;
}

const ProductGallery: React.FC<ProductGalleryProps> = ({ images, videos, shareAction }) => {
  // const [selectedImage, setSelectedImage] = useState(images[0]);
  const videoRef = useRef<HTMLVideoElement[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const thumbnailVideoRefs = useRef<HTMLVideoElement[]>([]);

  const allMedia: MediaItem[] = useMemo(() => {
    const parsedImages = images.map((image) => ({ type: MediaType.Image, url: image }));
    const parsedVideos = videos?.map((video) => ({ type: MediaType.Video, ...video })) ?? [];
    return [...parsedVideos, ...parsedImages];
  }, [images, videos]);

  // const handleImageClick = useCallback((image: string) => {
  //   setSelectedImage(image);
  // }, []);

  // useEffect(() => {
  //   setSelectedImage(images[0]);
  // }, [images]);

  const goPrev = () => {
    setSelectedIndex((prev) => (prev === 0 ? allMedia.length - 1 : prev - 1));
  };

  const goNext = () => {
    setSelectedIndex((prev) => (prev === allMedia.length - 1 ? 0 : prev + 1));
  };

  // Handle video playback
  const handleVideoPlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handleVideoPause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handleVideoEnded = useCallback(() => {
    setIsPlaying(false);
  }, []);

  // Set up event listeners for all videos
  useEffect(() => {
    const videos = videoRef.current;

    videos.forEach((video) => {
      if (video) {
        video.addEventListener("play", handleVideoPlay);
        video.addEventListener("pause", handleVideoPause);
        video.addEventListener("ended", handleVideoEnded);
      }
    });

    return () => {
      videos.forEach((video) => {
        if (video) {
          video.removeEventListener("play", handleVideoPlay);
          video.removeEventListener("pause", handleVideoPause);
          video.removeEventListener("ended", handleVideoEnded);
        }
      });
    };
  }, [handleVideoPlay, handleVideoPause, handleVideoEnded]);

  // Pause all videos when switching
  useEffect(() => {
    videoRef.current.forEach((video) => {
      if (video) {
        video.pause();
      }
    });
    setIsPlaying(false);
  }, [selectedIndex]);

  return (
    <div className="flex flex-col items-center space-y-4 w-full">
      <div className="relative w-full">
        <figure className="w-full pt-[100%] overflow-hidden relative group rounded-10 md:rounded-20">
          {allMedia[selectedIndex].type === MediaType.Image ? (
            <LazyImage
              src={allMedia[selectedIndex].url}
              alt="Main Product"
              loaderClasses="w-full h-full absolute top-0 left-0 rounded-10 md:rounded-20"
              className="w-full h-full absolute top-0 left-0 object-cover rounded-15 md:rounded-20 transition-transform duration-300 ease-out group-hover:scale-110 cursor-zoom-in"
            />
          ) : (
            <div className="bg-black absolute top-0 left-0 h-full w-full flex items-center justify-center">
              <video
                ref={(el) => (videoRef.current[selectedIndex] = el)}
                src={allMedia[selectedIndex].url}
                controls={false}
                loop
                muted={isMuted}
                className="max-w-full max-h-full relative z-[10]"
                playsInline
                autoPlay={selectedIndex === 0}
              />
              <div
                className="absolute top-0 left-0 w-full h-full z-[15] flex items-center justify-center cursor-pointer"
                onClick={() =>
                  isPlaying ? videoRef?.current[selectedIndex]?.pause() : videoRef?.current[selectedIndex]?.play()
                }
              >
                <button
                  type="button"
                  className={classNames(
                    "md:w-10 md:h-10 w-8 h-8 bg-[#000] backdrop-blur-sm bg-opacity-20 rounded-full flex items-center justify-center  duration-300 text-white",
                    { "opacity-0 group-hover:opacity-100 transition-opacity": isPlaying }
                  )}
                >
                  {isPlaying ? (
                    // prettier-ignore
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" >
                      <path d="M10.65 19.11V4.89C10.65 3.54 10.08 3 8.64 3H5.01C3.57 3 3 3.54 3 4.89V19.11C3 20.46 3.57 21 5.01 21H8.64C10.08 21 10.65 20.46 10.65 19.11Z" fill="currentColor"/>
                      <path d="M21.0016 19.11V4.89C21.0016 3.54 20.4316 3 18.9916 3H15.3616C13.9316 3 13.3516 3.54 13.3516 4.89V19.11C13.3516 20.46 13.9216 21 15.3616 21H18.9916C20.4316 21 21.0016 20.46 21.0016 19.11Z" fill="currentColor"/>
                    </svg>
                  ) : (
                    // prettier-ignore
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" >
                      <path d="M16.6593 15.8594L13.8051 17.5094L10.951 19.1595C7.27191 21.2777 4.26172 19.5385 4.26172 15.302V12.0019V8.70183C4.26172 4.44297 7.27191 2.72604 10.951 4.84433L13.8051 6.49436L16.6593 8.14439C20.3384 10.2627 20.3384 13.7411 16.6593 15.8594Z" fill="currentColor"/>
                    </svg>
                  )}
                </button>
              </div>
              <CanvasBgFromVideo videoRef={videoRef} index={selectedIndex} />
              <button
                type="button"
                onClick={() => setIsMuted((p) => !p)}
                className="md:w-10 md:h-10 w-8 h-8 absolute bottom-2.5 right-2.5 z-[999] bg-[#000] bg-opacity-20 rounded-full flex items-center justify-center  duration-300 text-white"
                // opacity-0 group-hover:opacity-100 transition-opacity
              >
                {isMuted ? (
                  // prettier-ignore
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" >
                      <path d="M18.0003 16.7503C17.8403 16.7503 17.6903 16.7003 17.5503 16.6003C17.2203 16.3503 17.1503 15.8803 17.4003 15.5503C18.6603 13.8703 18.9303 11.6403 18.1203 9.71032C17.9603 9.33032 18.1403 8.89032 18.5203 8.73032C18.9003 8.57032 19.3403 8.75032 19.5003 9.13032C20.5203 11.5503 20.1703 14.3603 18.6003 16.4603C18.4503 16.6503 18.2303 16.7503 18.0003 16.7503Z" fill="currentColor"/>
                      <path d="M19.8284 19.2492C19.6684 19.2492 19.5184 19.1992 19.3784 19.0992C19.0484 18.8492 18.9784 18.3792 19.2284 18.0492C21.3684 15.1992 21.8384 11.3792 20.4584 8.08922C20.2984 7.70922 20.4784 7.26922 20.8584 7.10922C21.2384 6.94922 21.6784 7.12922 21.8384 7.50922C23.4284 11.2892 22.8884 15.6692 20.4284 18.9492C20.2884 19.1492 20.0584 19.2492 19.8284 19.2492Z" fill="currentColor"/>
                      <path d="M14.0405 12.9581C14.6705 12.3281 15.7505 12.7781 15.7505 13.6681V16.5981C15.7505 18.3181 15.1305 19.6081 14.0205 20.2281C13.5705 20.4781 13.0705 20.5981 12.5505 20.5981C11.7505 20.5981 10.8905 20.3281 10.0105 19.7781L9.37047 19.3781C8.83047 19.0381 8.74047 18.2781 9.19047 17.8281L14.0405 12.9581Z" fill="currentColor"/>
                      <path d="M21.77 2.22891C21.47 1.92891 20.98 1.92891 20.68 2.22891L15.73 7.17891C15.67 5.57891 15.07 4.37891 14.01 3.78891C12.89 3.16891 11.46 3.32891 10 4.23891L7.09 6.05891C6.89 6.17891 6.66 6.24891 6.43 6.24891H5.5H5C2.58 6.24891 1.25 7.57891 1.25 9.99891V13.9989C1.25 16.4189 2.58 17.7489 5 17.7489H5.16L2.22 20.6889C1.92 20.9889 1.92 21.4789 2.22 21.7789C2.38 21.9189 2.57 21.9989 2.77 21.9989C2.97 21.9989 3.16 21.9189 3.31 21.7689L21.77 3.30891C22.08 3.00891 22.08 2.52891 21.77 2.22891Z" fill="currentColor"/>
                    </svg>
                ) : (
                  // prettier-ignore
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                    <path d="M18.0003 16.7503C17.8403 16.7503 17.6903 16.7003 17.5503 16.6003C17.2203 16.3503 17.1503 15.8803 17.4003 15.5503C18.9703 13.4603 18.9703 10.5403 17.4003 8.45027C17.1503 8.12027 17.2203 7.65027 17.5503 7.40027C17.8803 7.15027 18.3503 7.22027 18.6003 7.55027C20.5603 10.1703 20.5603 13.8303 18.6003 16.4503C18.4503 16.6503 18.2303 16.7503 18.0003 16.7503Z" fill="currentColor"/>
                    <path d="M19.8284 19.2503C19.6684 19.2503 19.5184 19.2003 19.3784 19.1003C19.0484 18.8503 18.9784 18.3803 19.2284 18.0503C21.8984 14.4903 21.8984 9.51027 19.2284 5.95027C18.9784 5.62027 19.0484 5.15027 19.3784 4.90027C19.7084 4.65027 20.1784 4.72027 20.4284 5.05027C23.4984 9.14027 23.4984 14.8603 20.4284 18.9503C20.2884 19.1503 20.0584 19.2503 19.8284 19.2503Z" fill="currentColor"/>
                    <path d="M14.02 3.78168C12.9 3.16168 11.47 3.32168 10.01 4.23168L7.09 6.06168C6.89 6.18168 6.66 6.25168 6.43 6.25168H5.5H5C2.58 6.25168 1.25 7.58168 1.25 10.0017V14.0017C1.25 16.4217 2.58 17.7517 5 17.7517H5.5H6.43C6.66 17.7517 6.89 17.8217 7.09 17.9417L10.01 19.7717C10.89 20.3217 11.75 20.5917 12.55 20.5917C13.07 20.5917 13.57 20.4717 14.02 20.2217C15.13 19.6017 15.75 18.3117 15.75 16.5917V7.41168C15.75 5.69168 15.13 4.40168 14.02 3.78168Z" fill="currentColor"/>
                    </svg>
                )}
              </button>
            </div>
          )}
        </figure>
        {shareAction && (
          <button
            className="text-black-placeholder absolute right-6.75 z-[18] bottom-6.75 no-outline rounded-full bg-grey-fields-100 p-2"
            onClick={shareAction}
          >
            {/* prettier-ignore */}
            <svg className="h-4.5 w-4.5 sm:h-5 sm:w-5" viewBox="0 0 22 22" fill="none">
              <path d="M15.0701 8.1582C18.3701 8.44237 19.7176 10.1382 19.7176 13.8507V13.9699C19.7176 18.0674 18.0768 19.7082 13.9793 19.7082H8.01177C3.91427 19.7082 2.27344 18.0674 2.27344 13.9699V13.8507C2.27344 10.1657 3.6026 8.46987 6.8476 8.16737" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M11 13.75V3.31836" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M14.0704 5.36234L10.9995 2.2915L7.92871 5.36234" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        )}
      </div>
      {allMedia.length > 1 && (
        <div className="flex w-full space-x-2 overflow-x-auto scrollbar-hide">
          {allMedia.map((item, index) => (
            <button
              key={index}
              onClick={() => setSelectedIndex(index)}
              className={`w-12.5 h-12.5 overflow-hidden rounded-10 border-2 transition-all duration-200 ease-out flex-shrink-0 relative ${
                selectedIndex === index ? "border-primary-500" : "border-grey-fields-200 hover:border-primary-500"
              } box-border`}
            >
              {item.type === MediaType.Image ? (
                <figure className="w-full h-full relative">
                  <LazyImage
                    src={item.type === MediaType.Image ? item.url : item.thumbnail}
                    loaderClasses="w-full h-full "
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </figure>
              ) : (
                <div className="w-full h-full absolute top-0 left-0 overflow-hidden">
                  <div className="absolute bg-grey-loader w-full h-full top-0 left-0 animate-pulse z-[-1]"></div>
                  <video
                    id="previewVideo"
                    src={`${item.url}#t=0.1`}
                    poster={item.thumbnail}
                    muted
                    preload="metadata"
                    className="w-full h-full object-cover z-[10]"
                    ref={(el) => (thumbnailVideoRefs.current[index] = el)}
                    playsInline
                  ></video>
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default React.memo(ProductGallery);
